import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  makeStyles,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Formik, Form } from 'formik'
import moment from 'moment-timezone'

import conferenceSlice, {
  fetchConferenceById,
  updateConferenceById,
  addConference,
  resetConferenceStore
} from 'store/conference'
import { confFormSchema } from 'schemas/conference'
import ViewHeader from 'components/ViewHeader'
import ProgressButton from 'components/ProgressButton'
import CustomBox from 'components/Form/CustomBox'
import CustomTextField from 'components/Form/CustomTextField'
import { Collection, Roles } from 'constants/index'
import DateTimeInput from 'components/DateTimeInput'
import { getDefaultTimezone } from 'utils/times'
import { useHistory, useParams } from 'react-router-dom'
import ActionBar from 'components/ActionBar'
import Autocomplete from '@material-ui/lab/Autocomplete'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import CustomSelect from 'components/Form/CustomSelect'
import { fetchSuperAdminConfiguration } from '../../../store/config'
import { updateSettingById } from '../../../store/settings'

const ImgKeys = {
  confMap: 'confMap',
  confSplashScreenImg: 'confSplashScreenImg',
  boothMap: 'boothMap',
  confLogo: 'confLogo'
}
const timeZonesOptions = moment.tz.names()

const ConferenceForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { conferenceId } = useParams()
  const { user } = useSelector((state) => state.auth)
  const { conference, conferences, inProgress } = useSelector((state) => state.conference)
  const [loading, setLoading] = useState(false)

  const previousConfs = useCallback(() => {
    if (conferenceId) {
      return conferences.filter((conf) => conf.id !== conferenceId)
    }
    return conferences
  }, [conferences, conferenceId])

  const [locationMapFile, setLocationMapFile] = useState(null)
  const [splashScreenFile, setSplashScreenFile] = useState(null)
  const [boothMapFile, setBoothMapFile] = useState(null)
  const defaultTimezone = getDefaultTimezone(conference?.timezone)

  const initialValues = {
    confName: conference?.confName ?? '',
    confCode: conference?.confCode ?? '',
    timezone: conference?.timezone ?? '',
    startDate: conference?.startDate
      ? moment(conference?.startDate).tz(conference?.timezone)
      : moment().tz(defaultTimezone),
    endDate: conference?.endDate
      ? moment(conference?.endDate).tz(conference?.timezone)
      : moment().tz(defaultTimezone),
    viewableUntil:
      conference?.viewableUntil && conference?.timezone
        ? moment(conference?.viewableUntil).tz(conference?.timezone)
        : conference?.endDate && conference?.timezone
        ? moment(conference?.endDate).tz(conference?.timezone)
        : moment().tz(defaultTimezone),
    is24hrFormat: conference?.is24hrFormat ?? false,
    isActive: conference?.isActive ?? true,
    exploreConfURL: conference?.exploreConfURL ?? '',
    exploreConfMenuItem: conference?.exploreConfMenuItem ?? '',
    abstractPrefix: conference?.abstractPrefix ?? '',
    posterPrefix: conference?.posterPrefix ?? '',
    havePolls: conference?.havePolls ?? false,
    enableBadgeModule: conference?.enableBadgeModule ?? false,
    hasPreviousConference: conference?.previousConferenceId ? 'yes' : 'no',
    previousConferenceId: conference?.previousConferenceId ?? ''
  }

  useEffect(() => {
    if (conferenceId) {
      dispatch(fetchConferenceById(conferenceId))
    }
    return () => {
      if (user?.role === Roles.SUPER_ADMIN) {
        dispatch(conferenceSlice.actions.setConference(null))
      }
    }
  }, [dispatch, conferenceId, user?.role])

  const handleFilesChange = (key) => (file) => {
    if (key === ImgKeys.confMap) {
      setLocationMapFile(file)
    } else if (key === ImgKeys.confSplashScreenImg) {
      setSplashScreenFile(file)
    } else {
      setBoothMapFile(file)
    }
  }

  const handleUpload = (key) => async (name) => {
    const modifiedConf = { ...conference }
    delete modifiedConf['id']
    if (key === ImgKeys.confMap) {
      modifiedConf[ImgKeys.confMap] = name
    } else if (key === ImgKeys.confSplashScreenImg) {
      modifiedConf[ImgKeys.confSplashScreenImg] = name
    } else if (key === ImgKeys.boothMap) {
      modifiedConf[ImgKeys.boothMap] = name
    } else {
      modifiedConf[ImgKeys.confLogo] = name
    }
    await dispatch(updateConferenceById(conference?.id, modifiedConf))
  }

  const handleDelete = async (key) => {
    const modifiedConf = { ...conference }
    delete modifiedConf['id']
    if (key === ImgKeys.confMap) {
      modifiedConf[ImgKeys.confMap] = null
    } else if (key === ImgKeys.confSplashScreenImg) {
      modifiedConf[ImgKeys.confSplashScreenImg] = null
    } else if (key === ImgKeys.boothMap) {
      modifiedConf[ImgKeys.boothMap] = null
    } else {
      modifiedConf[ImgKeys.confLogo] = null
    }
    await dispatch(updateConferenceById(conference?.id, modifiedConf))
  }

  const goBack = () => {
    dispatch(resetConferenceStore())
    history.goBack()
  }

  const onSubmit = async ({
    startDate,
    endDate,
    viewableUntil,
    confCode,
    timezone,
    confName,
    is24hrFormat,
    isActive,
    exploreConfURL,
    exploreConfMenuItem,
    abstractPrefix,
    posterPrefix,
    havePolls,
    enableBadgeModule,
    hasPreviousConference,
    previousConferenceId
  }) => {
    const payload = {
      havePolls: havePolls,
      startDate: typeof startDate === 'number' ? startDate : startDate.startOf('day').valueOf(),
      endDate: typeof endDate === 'number' ? endDate : endDate.endOf('day').valueOf(),
      viewableUntil:
        typeof viewableUntil === 'number' ? viewableUntil : viewableUntil.endOf('day').valueOf(),
      confCode: confCode.trim(),
      timezone: timezone.trim(),
      confName: confName.trim(),
      is24hrFormat,
      isActive,
      exploreConfURL,
      exploreConfMenuItem,
      abstractPrefix: abstractPrefix.toUpperCase(),
      posterPrefix: posterPrefix.toUpperCase(),
      enableBadgeModule,
      previousConferenceId: hasPreviousConference === 'yes' ? previousConferenceId : ''
    }

    if (conferenceId) {
      const otherConferences = conferences.filter((row) => row.id !== conferenceId)
      const sameConf = otherConferences.find((row) => row.confCode === payload.confCode)
      if (sameConf) {
        toast.warning(`Conference with same code already exists.`)
      } else {
        setLoading(true)
        await dispatch(updateConferenceById(conferenceId, payload))
        if (user.role === Roles.ADMIN) {
          dispatch(fetchConferenceById(conferenceId))
        }
        toast.success(`Conference updated successfully.`)
        setLoading(false)
      }
    } else {
      const sameConf = conferences.find((row) => row.confCode === payload.confCode)
      if (sameConf) {
        toast.warning(`Conference with same code already exists.`)
      } else {
        try {
          setLoading(true)
          const confId = await dispatch(addConference(payload))
          const configuration = await fetchSuperAdminConfiguration()
          dispatch(
            updateSettingById(
              confId,
              {
                ...configuration?.emailTemplates
              },
              () => toast.success('Default email templates copied to new conference successfully.'),
              () => toast.error('Failed copying the default email templates. Please try again.')
            )
          )

          if (locationMapFile) {
            payload.confMap = await uploadImageFirstTime(
              locationMapFile,
              Collection.CONFERENCES,
              confId
            )
          }
          if (splashScreenFile) {
            payload.confSplashScreenImg = await uploadImageFirstTime(
              splashScreenFile,
              Collection.CONFERENCES,
              confId
            )
          }
          if (boothMapFile) {
            payload.boothMap = await uploadImageFirstTime(
              boothMapFile,
              Collection.CONFERENCES,
              confId
            )
          }
          if (locationMapFile || splashScreenFile || boothMapFile) {
            await dispatch(updateConferenceById(confId, payload))
          }
          toast.success(`Conference created successfully.`)
        } catch (err) {
          // do nothing
        } finally {
          setLoading(false)
        }
      }
    }
    if (user.role === Roles.SUPER_ADMIN) {
      goBack()
    }
  }

  return (
    <CustomBox>
      <ViewHeader title={conferenceId ? 'Edit Conference' : 'Create Conference'}>
        {user.role === Roles.SUPER_ADMIN && <ActionBar actionText="Back" onAction={goBack} />}
      </ViewHeader>
      {conferenceId && inProgress ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <CircularProgress />
        </Box>
      ) : (
        <Formik
          enableReinitialize={!!conferenceId}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={confFormSchema}>
          {({ setFieldValue, values, errors, touched }) => (
            <Form>
              <Grid container spacing={2} className={classes.box}>
                <Grid item xs={12}>
                  <Typography variant="h6" color="secondary">
                    Conference Details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <CustomTextField label="Conference Name" name="confName" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Conference Code" name="confCode" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    disableClearable
                    options={timeZonesOptions}
                    getOptionLabel={(option) => option}
                    value={values.timezone}
                    onChange={(event, value) => {
                      if (value) {
                        setFieldValue('timezone', value)
                        setFieldValue('startDate', moment(values.startDate).tz(value))
                        setFieldValue('endDate', moment(values.endDate).tz(value))
                        setFieldValue('viewableUntil', moment(values.viewableUntil).tz(value))
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="timezone"
                        error={!!(touched.timezone && errors.timezone)}
                        helperText={touched.timezone && errors.timezone ? errors.timezone : ''}
                        label="Timezone"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container alignItems="center">
                    <Grid item xs={12} md={3}>
                      <DateTimeInput
                        value={values.startDate}
                        timeLabel="Start Date"
                        name="startDate"
                        minDate={moment().tz(defaultTimezone)}
                        onChange={(value) => {
                          setFieldValue('startDate', value)
                        }}
                        showTime={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DateTimeInput
                        value={values.endDate}
                        timeLabel="End Date"
                        name="endDate"
                        minDate={values.startDate}
                        onChange={(value) => {
                          setFieldValue('endDate', value)
                          setFieldValue('viewableUntil', value)
                        }}
                        showTime={false}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <DateTimeInput
                        value={values.viewableUntil}
                        timeLabel="Viewable Until"
                        name="viewableUntil"
                        minDate={values.endDate}
                        onChange={(value) => setFieldValue('viewableUntil', value)}
                        showTime={false}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        label="Use 24hr Format"
                        control={
                          <Checkbox
                            name="is24hrFormat"
                            onChange={(e) => setFieldValue('is24hrFormat', e.target.checked)}
                            checked={values.is24hrFormat}
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {user?.role === Roles.SUPER_ADMIN && (
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          className={classes.formControlLabel}
                          labelPlacement="start"
                          label="Enable polling module"
                          control={
                            <Switch
                              className={classes.switch}
                              name="havePolls"
                              onChange={(e) => setFieldValue('havePolls', e.target.checked)}
                              checked={values.havePolls}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          className={classes.formControlLabel}
                          labelPlacement="start"
                          label="Enable badge module"
                          control={
                            <Switch
                              className={classes.switch}
                              name="enableBadgeModule"
                              onChange={(e) => setFieldValue('enableBadgeModule', e.target.checked)}
                              checked={values.enableBadgeModule}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          className={classes.formControlLabel}
                          labelPlacement="start"
                          label="Is Active"
                          control={
                            <Switch
                              className={classes.switch}
                              name="isActive"
                              onChange={(e) => setFieldValue('isActive', e.target.checked)}
                              checked={values.isActive}
                            />
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box className={classes.radioGroup}>
                          <Typography className={classes.radioLabel}>
                            Do you want to link this conference to previous one?
                          </Typography>
                          <RadioGroup
                            row
                            value={values.hasPreviousConference}
                            onChange={(e) =>
                              setFieldValue('hasPreviousConference', e.target.value)
                            }>
                            <FormControlLabel
                              control={<Radio size="small" />}
                              label={<Typography variant="body2">Yes</Typography>}
                              value="yes"
                            />
                            <FormControlLabel
                              control={<Radio size="small" />}
                              label={<Typography variant="body2">No</Typography>}
                              value="no"
                            />
                          </RadioGroup>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {values.hasPreviousConference === 'yes' && (
                          <CustomSelect
                            inputLabel="Previous Conference"
                            options={previousConfs()}
                            optionAttr="confName"
                            name="previousConferenceId"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              <Grid container spacing={2} className={classes.box}>
                <Grid item xs={12}>
                  <Typography variant="h6" color="secondary">
                    Conference Settings
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Explore Conference Menu Item"
                    name="exploreConfMenuItem"
                    type="text"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Explore Conference URL"
                    name="exploreConfURL"
                    type="text"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Abstract Number Prefix"
                    name="abstractPrefix"
                    type="text"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <CustomTextField label="Poster Number Prefix" name="posterPrefix" type="text" />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={6}>
                    <AvatarPicker
                      title="Location Map"
                      subTitle="Allowed Format: png/jpg"
                      path={Collection.CONFERENCES}
                      handleCustomChange={(file) => handleFilesChange(ImgKeys.confMap)(file)}
                      onUpload={(name) => handleUpload(ImgKeys.confMap)(name)}
                      onDelete={() => handleDelete(ImgKeys.confMap)}
                      imageName={conference?.id === conferenceId ? conference?.confMap : null}
                      conferenceId={conference?.id === conferenceId ? conferenceId : null}
                      firsTimeUpload={!conferenceId}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <AvatarPicker
                      title="Splash Screen"
                      subTitle="Allowed Format: png/jpg ; Dimensions : 640x320"
                      path={Collection.CONFERENCES}
                      handleCustomChange={(file) =>
                        handleFilesChange(ImgKeys.confSplashScreenImg)(file)
                      }
                      onUpload={(name) => handleUpload(ImgKeys.confSplashScreenImg)(name)}
                      onDelete={() => handleDelete(ImgKeys.confSplashScreenImg)}
                      imageName={
                        conference?.id === conferenceId ? conference?.confSplashScreenImg : null
                      }
                      conferenceId={conference?.id === conferenceId ? conferenceId : null}
                      firsTimeUpload={!conferenceId}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <AvatarPicker
                      title="Booth Map"
                      subTitle="Allowed Format: png/jpg"
                      path={Collection.CONFERENCES}
                      handleCustomChange={(file) => handleFilesChange(ImgKeys.boothMap)(file)}
                      onUpload={(name) => handleUpload(ImgKeys.boothMap)(name)}
                      onDelete={() => handleDelete(ImgKeys.boothMap)}
                      imageName={conference?.id === conferenceId ? conference?.boothMap : null}
                      conferenceId={conference?.id === conferenceId ? conferenceId : null}
                      firsTimeUpload={!conferenceId}
                    />
                  </Grid>

                  <Grid item xs={12} lg={6}>
                    <AvatarPicker
                      title="Conference Logo"
                      subTitle="Allowed Format: png/jpg"
                      path={Collection.CONFERENCES}
                      handleCustomChange={(file) => handleFilesChange(ImgKeys.confLogo)(file)}
                      onUpload={(name) => handleUpload(ImgKeys.confLogo)(name)}
                      onDelete={() => handleDelete(ImgKeys.confLogo)}
                      imageName={conference?.id === conferenceId ? conference?.confLogo : null}
                      conferenceId={conference?.id === conferenceId ? conferenceId : null}
                      firsTimeUpload={!conferenceId}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container justifyContent="flex-end" spacing={2} className={classes.actions}>
                {user.role === Roles.SUPER_ADMIN && (
                  <Grid item>
                    <Button size="large" disabled={loading} onClick={goBack}>
                      Cancel
                    </Button>
                  </Grid>
                )}
                <Grid item>
                  <ProgressButton
                    type="submit"
                    label="Save"
                    inProgress={loading}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </CustomBox>
  )
}
export default ConferenceForm

const useStyles = makeStyles(() => ({
  actions: {
    marginTop: '1rem'
  },
  radioGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  radioLabel: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#2f2f2f'
  },
  label: {
    fontSize: '15px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    marginRight: '1rem'
  },
  formControlLabel: {
    margin: '0px'
  },
  box: {
    border: '1px solid #757575',
    borderRadius: '12px',
    marginBottom: '36px',
    padding: '8px'
  },
  switch: {
    marginLeft: '16px'
  }
}))
